import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Rating } from "./components/Rating/Rating";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  MEETING_REPORTS_LIST_ENDPOINT,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllMeetingReports } from "./MeetingReportsList.actionCreators";
import { setAllMeetingReportsLoadingState } from "./MeetingReportsList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import MeetingAction from "./components/MeetingAction/MeetingAction";
import MeetingReportId from "./components/MeetingReportId/MeetingReportId";
import LinkedEntities from "./components/LinkedEntities/LinkedEntities";
import { columnHeader, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    objectType,
    objectId,
    columnList,
    createNewAction,
    type,
    mainCountryInfo,
    countryId,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);

  const countryList = UserInfo.countryListForFilter(t);

  const [selectedFilter, setSelectedFilter] = useState([
    {
      column: "country",
      value: {
        value: mainCountryInfo?.value,
        label: t(mainCountryInfo?.label),
      },
    },
  ]);
  const dataURL = MEETING_REPORTS_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [];

  columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      Cell: ({ cell: { value } }) => (
        <MeetingReportId
          id={value}
          updateRefeshStatus={updateRefeshStatus}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("TITLE"),
      title: "TITLE",
      accessor: "title",
      disableFilters: true,
    },
    {
      Header: t("HESUS_PARTICIPANTS"),
      title: "HESUS_PARTICIPANTS",
      accessor: "attendeeList",
      disableFilters: true,
    },
    {
      Header: t("MEETING_DATE"),
      title: "MEETING_DATE",
      accessor: "meetingDate",
      disableFilters: true,
      Cell: ({ value }) => getDateFormat(value, "YYYY-MM-DD"),
    },
    {
      Header: t("ENTITES_LINKED"),
      title: "ENTITES_LINKED",
      accessor: "linkedEntities",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <LinkedEntities data={value} />,
    },
    {
      Header: t("RATING"),
      title: "RATING",
      accessor: "rating",
      Cell: ({ cell: { value } }) => <Rating rating={value} />,
      disableFilters: true,
    },
    {
      Header: t("MEETING_NEXT_DATE"),
      title: "MEETING_NEXT_DATE",
      accessor: "nextStepDate",
      disableFilters: true,
      Cell: ({ value }) => getDateFormat(value, "YYYY-MM-DD"),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <MeetingAction
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const meetingReportsListByIds = useSelector(
    (state) => state.meetingReportsList.byId
  );
  const meetingReportsListAllIds = useSelector(
    (state) => state.meetingReportsList.allIds
  );
  const filteredRecords = useSelector(
    (state) => state.meetingReportsList.filteredRecords
  );

  const isMeetingReportsListLoading = useSelector(
    (state) => state.meetingReportsList.isLoading
  );

  useEffect(() => {
    setLoading(isMeetingReportsListLoading);
  }, [isMeetingReportsListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    let selectedCountries = [];
    if (additionalFilters.length > 0) {
      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      selectedCountries = countryTypeFilter?.map((item) => item.value);
    }
    if (selectedCountries.length === 0) {
      selectedCountries = countryId;
    }
    // Set the loading state
    setLoading(true);

    dispatch(setAllMeetingReportsLoadingState());
    dispatch(
      loadAllMeetingReports({
        dataURL,
        objectType,
        objectId,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status: 1,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    if (meetingReportsListAllIds.length > 0) {
      const responseData = meetingReportsListAllIds.map((id) => {
        const tempData = { ...meetingReportsListByIds[id] };
        if (tempData?.attendees) {
          tempData.attendeeList = tempData.attendees
            .reduce((p, c) => [...p, c?.firstName + " " + c?.lastName], [])
            .join(", ");
        }
        tempData.action = {
          id: tempData.id,
          canEdit: tempData.canEdit,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, meetingReportsListAllIds, meetingReportsListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: additionalFilters,
      });
    }
  }, [additionalFilters]);

  return (
    <EncloseTable
      type={type}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={30}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      createNewAction={createNewAction}
      selectedFilter={selectedFilter}
      extraFilters={[
        ...(countryList?.length > 1 && objectType !== "company"
          ? [
              {
                type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                label: t("COUNTRY"),
                name: "country",
                labelClass: "company-list__admins-list-label",
                containerClass: "company-list__admins-list-dropdown-container",
                options: countryList,
                placeholder: t("SEARCH"),
                defaultOption: "Nothing Selected",
              },
            ]
          : []),
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  createNewAction: PropTypes.func,
  countryId: PropTypes.number,
};

PrepareDataTable.defaultProps = {
  createNewAction: null,
  countryId: null,
};

export default withTranslation()(PrepareDataTable);
